import { Flex, Select } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { Button } from '@main/complykit/components/button';
import { ButtonGroup } from '@main/complykit/components/button-group';
import { IconButton } from '@main/complykit/components/icon-button';
import { Text } from '@main/complykit/components/text';
import { Table } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

export function shouldDisplayPagination<TData>(
  table: Table<TData>,
  pageSizeOptions: [number, ...number[]],
  hasNextPage?: boolean,
) {
  return getPaginationDisplayType(table, pageSizeOptions, hasNextPage) !== 'none';
}

const minimimItemsToShowPagination = 5;

function getPaginationDisplayType<TData>(
  table: Table<TData>,
  pageSizeOptions: [number, ...number[]],
  hasNextPage?: boolean,
) {
  if (hasNextPage !== undefined) {
    const { pageIndex } = table.getState().pagination;
    if (pageIndex === 0 && !hasNextPage) {
      const rowsOnPage = table.getFilteredRowModel().rows.length;
      return rowsOnPage <= minimimItemsToShowPagination ? 'none' : 'compact';
    }
    return 'full';
  }

  const totalRows = table.getRowCount();
  const smallestPageSize = Math.min(...pageSizeOptions);
  const onlyOnePage = totalRows <= smallestPageSize;
  if (onlyOnePage) {
    if (totalRows <= minimimItemsToShowPagination) {
      return 'none';
    }
    return 'compact';
  }
  return 'full';
}

export function TablePagination<TData>({
  table,
  pageSizeOptions,
  hasNextPage,
}: {
  table: Table<TData>;
  pageSizeOptions: [number, ...number[]];
  hasNextPage?: boolean;
}) {
  const { t } = useTranslation('ui');
  const { pageSize, startingRow, endingRow } = getPaginationInfo(table, hasNextPage);
  const unknownTotal = hasNextPage !== undefined;
  const displayType = getPaginationDisplayType(table, pageSizeOptions, hasNextPage);
  return displayType === 'none' ? null : displayType === 'compact' ? (
    <Flex p={3} align="center" justify="end">
      <Text size="xs">
        <Text as="span" size="xs" weight="medium">
          {startingRow} - {endingRow}
        </Text>{' '}
        {t('table.pagination.of')} {endingRow}
      </Text>
    </Flex>
  ) : (
    <Flex p={3} align="center" justify="space-between">
      <Select
        size="sm"
        value={pageSize}
        variant="outline"
        onChange={(e) => table.setPageSize(parseInt(e.target.value, 10))}
        w="auto"
        aria-label={t('table.pagination.rowsPerPage')}
      >
        {pageSizeOptions.map((size) => (
          <option key={size} value={size}>
            {size} {t('table.pagination.perPage')}
          </option>
        ))}
      </Select>
      <ButtonGroup label={t('table.pagination.label')}>
        <Flex px={2} align="center">
          <Text size="sm">
            <Text as="span" size="sm" weight="medium">
              {startingRow} - {endingRow}
            </Text>
            {!unknownTotal && (
              <>
                {' '}
                {t('table.pagination.of')} {table.getRowCount()}
              </>
            )}
          </Text>
        </Flex>
        {!unknownTotal && (
          <Button
            variant="ghost"
            isDisabled={!table.getCanPreviousPage()}
            onPress={() => table.firstPage()}
          >
            {t('table.pagination.first')}
          </Button>
        )}
        <IconButton
          icon={ChevronLeftIcon}
          label={t('table.pagination.previous')}
          isDisabled={!table.getCanPreviousPage()}
          onPress={() => table.previousPage()}
        />
        <IconButton
          icon={ChevronRightIcon}
          label={t('table.pagination.next')}
          isDisabled={!table.getCanNextPage()}
          onPress={() => table.nextPage()}
        />

        {!unknownTotal && (
          <Button
            variant="ghost"
            isDisabled={!table.getCanNextPage()}
            onPress={() => table.lastPage()}
          >
            {t('table.pagination.last')}
          </Button>
        )}
      </ButtonGroup>
    </Flex>
  );
}

function getPaginationInfo<TData>(table: Table<TData>, hasNextPage?: boolean) {
  const { pageSize, pageIndex } = table.getState().pagination;

  const totalRows = table.getRowCount();
  const startingRow = pageSize * pageIndex + 1;
  const endingRow =
    hasNextPage !== undefined
      ? pageSize * pageIndex + table.getRowModel().rows.length
      : Math.min(pageSize * (pageIndex + 1), totalRows);

  return {
    pageSize,
    startingRow,
    endingRow,
  };
}
